import { pictureOptions } from '@/utils/consts';
import isEmpty from 'lodash/isEmpty';
import throttle from 'lodash/throttle';
import debounce from 'lodash/debounce';
import * as Sentry from '@sentry/nextjs';

// checkServer
export const checkServer = () => typeof window === 'undefined';

// 资源路径
export const handleUrl = (url?: string, option?: { w?: number; h?: number; q?: number }) => {
  if (!url) return '';
  // 本地路径
  if (/^\/static/.test(url)) return url;
  // 测试环境 upload 路径
  if (/uploads\.01/.test(url))
    return url.replace(/(http(s)?:)?\/\/uploads\.01/, 'https://uploads01');
  // 不做cdn压缩
  if (isEmpty(option)) return url;
  return `${url}${/\?/.test(url) ? '&' : '?'}x-oss-process=image/resize${
    option?.w ? `,w_${option.w}` : ''
  }${option?.h ? `,h_${option.h}` : ''}${option?.q ? `/quality,q_${option.q}` : ''}`;
};

// waitTime
export const waitTime = (time: number = 100) => {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve(true);
    }, time);
  });
};

// 异步节流
export function asyncThrottle<T>(func, wait) {
  const throttled = throttle((resolve, reject, args) => {
    func(...args)
      .then(resolve)
      .catch(reject);
  }, wait);
  return (...args) =>
    new Promise<T>((resolve, reject) => {
      throttled(resolve, reject, args);
    });
}

// 异步防抖
export function asyncDebounce<T>(func, wait) {
  const debounced = debounce((resolve, reject, args) => {
    func(...args)
      .then(resolve)
      .catch(reject);
  }, wait);
  return (...args) =>
    new Promise<T>((resolve, reject) => {
      debounced(resolve, reject, args);
    });
}

// 截图
export const screenshot = (
  source: CanvasImageSource | null,
  options: { width: number; height: number; isCamera?: boolean } = {
    ...pictureOptions,
    isCamera: false,
  },
) => {
  return new Promise<{ src: string; file: Blob | null }>((resolve) => {
    if (!source) {
      resolve({ src: '', file: null });
      return;
    }
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    canvas.width = options.width;
    canvas.height = options.height;
    if (options.isCamera) {
      // 摄像头需要水平镜像翻转
      context?.translate(canvas.width, 0);
      context?.scale(-1, 1);
    }
    context?.drawImage(source, 0, 0, options.width, options.height);
    canvas.toBlob(function (file) {
      const url = file ? URL.createObjectURL(file) : '';
      resolve({ src: url, file });
    }, 'image/jpeg');
  });
};

/**
 *
 * @param obj 路由跳转的参数对象，如果key设置为undefined则删除这个参数
 * @param pathname 定义的域名、子域名
 * @returns
 */
export const handleParam = (obj?: Record<string, string | undefined>, pathname?: string) => {
  const paramsArray = window.location.search.replace('?', '').split('&');
  const params = paramsArray.reduce((pre, item) => {
    const [k, v] = item.split('=');
    pre[k] = v;
    return pre;
  }, {});

  const newObj = { ...params, ...obj };
  const res: string[] = [];
  Object.keys(newObj).forEach((k) => {
    if (newObj[k] === undefined) {
      Reflect.deleteProperty(newObj, k);
    } else {
      res.push(`${k}=${newObj[k]}`);
    }
  });

  return pathname ? `${pathname}?${res.join('&')}` : `${window.location.pathname}?${res.join('&')}`;
};

export const tryCatch = (fn: Function, onError?: Function) => {
  try {
    fn();
  } catch (err) {
    console.log(`err`, err);
    Sentry.captureException(err);
    onError?.(err);
  }
};

export function to<T, U = Error>(
  promise: Promise<T>,
  errorExt?: object,
): Promise<[U, undefined] | [null, T]> {
  return promise
    .then<[null, T]>((data: T) => [null, data])
    .catch<[U, undefined]>((err: U) => {
      if (errorExt) {
        const parsedError = Object.assign({}, err, errorExt);
        return [parsedError, undefined];
      }

      return [err, undefined];
    });
}

/**
 * 请求错误重发
 * @param fetcher
 * @param args
 * @returns
 */
export function fetchWithAutoRetry<T>(
  fetcher: (...arg: any) => Promise<T>,
  ...args: any
): Promise<T> {
  const maximumRetryCount = 2;

  return new Promise((resolve, reject) => {
    function _fetcher(last) {
      return fetcher(...args)
        .then((res: T) => {
          resolve(res);
        })
        .catch((err: any) => {
          if (last === 0) {
            reject(err);
          } else {
            setTimeout(() => {
              _fetcher(last - 1);
            }, 300);
          }
        });
    }

    _fetcher(maximumRetryCount);
  });
}

export const cConsole = (flag: string, data: any, color?: string) => {
  console.log(`%c${flag}`, `color: ${color ? color : 'red'};font-size:30px`, data);
};
